import { ShieldWarningIcon } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { toast } from 'sonner';
import z from 'zod';

import { Button } from '@components/ui/button';
import { endBrowserSession } from '@helpers/signOut.helper';

const unauthorizedSearchSchema = z.looseObject({
  error: z.string().optional(),
  error_description: z.string().optional(),
  reason: z.enum(['login-failed']).optional(),
});

export const Route = createFileRoute('/unauthorized')({
  component: Unauthorized,
  validateSearch: search => unauthorizedSearchSchema.parse(search),
});

function Unauthorized() {
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { error, error_description, reason } = Route.useSearch();

  const { isPending, mutate: switchAccount } = useMutation({
    mutationFn: endBrowserSession,
    onError: () => {
      toast.error('Warning', {
        description: 'Could not sign out. Please try again.',
      });
    },
    onSuccess: result => {
      queryClient.clear();

      if (result === 'local') {
        navigate({ replace: true, to: '/login' });
      }
    },
  });

  return (
    <main className="bg-background grid min-h-dvh place-items-center px-6 text-center">
      <div className="grid max-w-md gap-5">
        <ShieldWarningIcon className="text-secondary mx-auto size-12" />
        <div className="grid gap-2">
          <h1 className="text-primary-dark-one text-3xl font-semibold">
            Unauthorized
          </h1>
          <p className="text-muted text-sm">
            {reason === 'login-failed'
              ? 'We could not sign you in.'
              : 'Your account is not allowed to use the Voice Agent.'}{' '}
            Access requires an active Jaimy Connect admin account with the
            Manager or Operator role. Contact your administrator if you need
            access.
          </p>
          {(error || error_description) && (
            <p className="text-muted text-xs wrap-break-word">
              {error_description ?? error}
            </p>
          )}
        </div>
        <Button
          className="mx-auto mt-4 w-fit px-8"
          isLoading={isPending}
          onClick={() => switchAccount()}
          variant="secondary"
        >
          Sign in with another account
        </Button>
      </div>
    </main>
  );
}
